import { useCallback, useEffect, useState } from "react";
import { ApiError, apiFetch } from "../api/client";

export type BoardAccessMember = { user_id: string; email: string; role: string };
export type BoardAccessInvitation = {
  id: string;
  email: string;
  role: string;
  expires_at: string;
};
export type BoardShareLink = {
  id: string;
  role: string;
  uses_count: number;
  max_uses: number | null;
  expires_at: string | null;
  revoked_at: string | null;
};

export function useBoardAccess(boardId: string | undefined, enabled: boolean) {
  const [loading, setLoading] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [myUserId, setMyUserId] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [members, setMembers] = useState<BoardAccessMember[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<BoardAccessInvitation[]>([]);
  const [links, setLinks] = useState<BoardShareLink[]>([]);
  const [guestPolicy, setGuestPolicy] = useState("private");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const flashOk = useCallback((msg: string) => {
    setOk(msg);
    setErr("");
    window.setTimeout(() => setOk(""), 3500);
  }, []);

  const reloadMembers = useCallback(async () => {
    if (!boardId) return;
    const [me, membersData, boardData] = await Promise.all([
      apiFetch<{ id: string }>("/api/v1/me"),
      apiFetch<{
        owner: { user_id: string; email: string };
        members: BoardAccessMember[];
        pending_invitations?: BoardAccessInvitation[];
      }>(`/api/v1/boards/${boardId}/members`),
      apiFetch<{ board: { guest_policy?: string } }>(`/api/v1/boards/${boardId}`),
    ]);
    const ownerMode = membersData.owner.user_id === me.id;
    setMyUserId(me.id);
    setIsOwner(ownerMode);
    setIsMember(!ownerMode && (membersData.members || []).some((m) => m.user_id === me.id));
    setOwnerEmail(membersData.owner.email);
    setMembers(membersData.members || []);
    setPendingInvitations(membersData.pending_invitations || []);
    setGuestPolicy(boardData.board.guest_policy || "private");
    if (ownerMode) {
      const linksData = await apiFetch<{ shareLinks: BoardShareLink[] }>(
        `/api/v1/boards/${boardId}/share-links`
      );
      setLinks(linksData.shareLinks || []);
    } else {
      setLinks([]);
    }
  }, [boardId]);

  const reload = useCallback(async () => {
    if (!boardId || !enabled) return;
    setLoading(true);
    setErr("");
    try {
      await reloadMembers();
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        setIsOwner(false);
        setIsMember(false);
      } else {
        setErr(e instanceof Error ? e.message : "Ошибка загрузки");
      }
    } finally {
      setLoading(false);
    }
  }, [boardId, enabled, reloadMembers]);

  useEffect(() => {
    if (enabled && boardId) void reload();
  }, [enabled, boardId, reload]);

  const addMember = useCallback(
    async (email: string, role: string) => {
      if (!boardId) return;
      const res = await apiFetch<{
        member?: BoardAccessMember;
        invitation?: BoardAccessInvitation & { mail_sent?: boolean };
      }>(`/api/v1/boards/${boardId}/members`, {
        method: "POST",
        json: { email: email.trim(), role },
      });
      if (res.invitation) {
        flashOk(
          res.invitation.mail_sent
            ? "Приглашение отправлено на email"
            : "Приглашение создано (передайте ссылку вручную)"
        );
      } else {
        flashOk("Участник добавлен");
      }
      await reloadMembers();
    },
    [boardId, flashOk, reloadMembers]
  );

  const updateMemberRole = useCallback(
    async (userId: string, role: string) => {
      if (!boardId) return;
      await apiFetch(`/api/v1/boards/${boardId}/members/${userId}`, {
        method: "PATCH",
        json: { role },
      });
      flashOk("Роль обновлена");
      await reloadMembers();
    },
    [boardId, flashOk, reloadMembers]
  );

  const removeMember = useCallback(
    async (userId: string) => {
      if (!boardId) return;
      await apiFetch(`/api/v1/boards/${boardId}/members/${userId}`, { method: "DELETE" });
      flashOk("Участник удалён");
      await reloadMembers();
    },
    [boardId, flashOk, reloadMembers]
  );

  const revokeInvitation = useCallback(
    async (invitationId: string) => {
      if (!boardId) return;
      await apiFetch(`/api/v1/boards/${boardId}/invitations/${invitationId}`, {
        method: "DELETE",
      });
      flashOk("Приглашение отозвано");
      await reloadMembers();
    },
    [boardId, flashOk, reloadMembers]
  );

  const createShareLink = useCallback(
    async (role: string, maxUses?: number) => {
      if (!boardId) return null;
      const body: { role: string; max_uses?: number } = { role };
      if (maxUses != null && maxUses > 0) body.max_uses = maxUses;
      const data = await apiFetch<{ shareLink: { openPath?: string } }>(
        `/api/v1/boards/${boardId}/share-links`,
        { method: "POST", json: body }
      );
      flashOk("Ссылка создана");
      await reloadMembers();
      return `${window.location.origin}${data.shareLink.openPath || ""}`;
    },
    [boardId, flashOk, reloadMembers]
  );

  const revokeShareLink = useCallback(
    async (linkId: string) => {
      if (!boardId) return;
      await apiFetch(`/api/v1/boards/${boardId}/share-links/${linkId}`, { method: "DELETE" });
      flashOk("Ссылка отозвана");
      await reloadMembers();
    },
    [boardId, flashOk, reloadMembers]
  );

  const saveGuestPolicy = useCallback(
    async (policy: string) => {
      if (!boardId) return;
      await apiFetch(`/api/v1/boards/${boardId}`, {
        method: "PATCH",
        json: { guest_policy: policy },
      });
      setGuestPolicy(policy);
      flashOk("Политика гостей сохранена");
    },
    [boardId, flashOk]
  );

  const leaveBoard = useCallback(async () => {
    if (!boardId || !myUserId) return;
    await apiFetch(`/api/v1/boards/${boardId}/members/${myUserId}`, { method: "DELETE" });
  }, [boardId, myUserId]);

  return {
    loading,
    isOwner,
    isMember,
    myUserId,
    ownerEmail,
    members,
    pendingInvitations,
    links,
    guestPolicy,
    err,
    ok,
    setErr,
    reload,
    addMember,
    updateMemberRole,
    removeMember,
    revokeInvitation,
    createShareLink,
    revokeShareLink,
    saveGuestPolicy,
    leaveBoard,
  };
}
